import { useLingui } from '@lingui/react/macro';

import { useProducts } from '@/models/product';
import { getRootProductAttributes } from '@/models/productAttribute';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';

type InputShownOptional = {
  shown: boolean;
  disabled: boolean;
  disabledTooltipMessage?: string;
  // When creating an article, either the unique article or the product or a root product attribute must be selected.
  // Therefore, those fields are individually neither required nor optional.
  // But they become optional when a root product attribute is required in config, or one of the others is selected.
  optional: boolean;
};

type ProductAndUniqueArticleInputs = {
  uniqueArticleInput: InputShownOptional;
  productInput: InputShownOptional;
  errorMessage: string;
};

/** Returns shown / disabled / optional state for product and serial-number inputs, plus a shared error message. */
export const useProductAndUniqueArticleInputs = (): ProductAndUniqueArticleInputs => {
  const { t } = useLingui();
  const { article, view, productAttributes } = useArticleContext();

  const hasUniqueArticle = !!article.uniqueArticle;
  const hasProduct = !!article.product;
  const hasUniqueArticleBeenManuallyCreated =
    article.uniqueArticle?.createdFromArticleId === article.id;
  const hasProductFromPurchaseHistory = !!article?.dataFromPurchaseHistory?.productId;

  const productDisabled =
    (hasUniqueArticle && !hasUniqueArticleBeenManuallyCreated) || hasProductFromPurchaseHistory;

  const { data: { products } = { products: [] }, isFetching: isFetchingProducts } = useProducts(
    {
      brandId: article?.brandId ?? undefined,
      limit: 10,
    },
    {
      keepPreviousData: true,
      enabled: !productDisabled,
    }
  );

  const productShown =
    view.details.product.canCreate ||
    isFetchingProducts ||
    !!products?.length ||
    (productDisabled && !!article.productId);

  const uniqueArticleShown = view.details.serialNumber.shown;
  const uniqueArticleRequired = view.details.serialNumber.required;

  const hasUniqueArticleFromPurchaseHistory = !!article?.dataFromPurchaseHistory?.serialNumber;
  const hasSerialNumberEnabledOnProduct = !!article.product?.serialNumberEnabled;
  const uniqueArticleDisabled =
    hasUniqueArticleFromPurchaseHistory || (hasProduct && !hasSerialNumberEnabledOnProduct);

  const rootProductAttributes = getRootProductAttributes({
    productAttributes: productAttributes ?? [],
  });

  const hasRequiredRootProductAttribute = rootProductAttributes.some(
    (attribute) => attribute.enabled && attribute.required
  );

  const hasProductAttributeSelected = !!article.productAttributeValues.length;

  const productAndUniqueArticleInputs = {
    uniqueArticleInput: {
      shown: uniqueArticleShown,
      disabled: uniqueArticleDisabled,
      disabledTooltipMessage:
        hasProduct && !hasSerialNumberEnabledOnProduct
          ? t({
              id: 'article.form.unique-article.tooltip.product-has-no-serial-number',
              message: 'The selected product does not have a serial number',
            })
          : undefined,
      optional:
        !uniqueArticleRequired &&
        (hasRequiredRootProductAttribute || hasProductAttributeSelected || hasProduct),
    },
    productInput: {
      shown: productShown,
      disabled: productDisabled,
      optional: hasRequiredRootProductAttribute || hasProductAttributeSelected || hasUniqueArticle,
    },
  };

  const errorMessage = useProductAndUniqueArticleErrorMessage(productAndUniqueArticleInputs);

  return {
    ...productAndUniqueArticleInputs,
    errorMessage,
  };
};

const useProductAndUniqueArticleErrorMessage = ({
  uniqueArticleInput,
  productInput,
}: Omit<ProductAndUniqueArticleInputs, 'errorMessage'>): string => {
  const { t } = useLingui();

  const shouldShowUniqueArticleError =
    uniqueArticleInput.shown && !uniqueArticleInput.disabled && !uniqueArticleInput.optional;
  const shouldShowProductError =
    productInput.shown && !productInput.disabled && !productInput.optional;

  if (shouldShowUniqueArticleError && shouldShowProductError) {
    return t({
      id: 'article.form.external-id.error.serial-number',
      message: 'Please select a serial number, product reference or categorization',
    });
  }

  if (shouldShowProductError) {
    return t({
      id: 'article.form.external-id.error',
      message: 'Please select a product reference or categorization',
    });
  }

  if (shouldShowUniqueArticleError) {
    return t({
      id: 'article.form.external-id.error.serial-number-or-categorization',
      message: 'Please select a serial number or categorization',
    });
  }

  return t({
    id: 'article.form.external-id.error.categorization-only',
    message: 'Please select a categorization',
  });
};
